/**
 * Pure presentation logic for the Message builder: working copies, status
 * chips, rail rows, layout choices and the copy that explains them. No React,
 * no requests. Everything here works on API DTOs (`@v0y4ge/api-contracts`).
 */
import {
  studioMessageJourneyMomentAssetId,
  studioMessageJourneyMomentRowRef,
  type MessageLayoutSummary,
  type MessageTemplateContent,
  type MessageTemplateContentInput,
  type MessageTemplateDetail,
  type MessageTemplateLayoutBinding,
  type MessageTemplateSummary,
  type MessagingDefaultLayout,
} from "@v0y4ge/api-contracts";

export type Tr = (key: string, fallback: string) => string;
export type Tone = "neutral" | "info" | "success" | "warning" | "danger";
export interface Chip { tone: Tone; label: string }

/* ---------- working copies ---------- */

export interface EmailWork {
  channel: "email";
  subject: string;
  preheader: string;
  html: string;
  layout: MessageTemplateLayoutBinding;
  sections: { header: boolean; footer: boolean };
}
export interface SmsWork { channel: "sms"; text: string }
export type Work = EmailWork | SmsWork;

export function workOf(content: MessageTemplateContent | null | undefined, channel: "email" | "sms"): Work {
  if (content && content.channel === "email" && channel === "email") {
    const e = content.email;
    return { channel: "email", subject: e.subject, preheader: e.preheader, html: e.html, layout: e.layout, sections: { header: e.sections.header, footer: e.sections.footer } };
  }
  if (content && content.channel === "sms" && channel === "sms") return { channel: "sms", text: content.sms.text };
  return channel === "sms"
    ? { channel: "sms", text: "" }
    : { channel: "email", subject: "", preheader: "", html: "", layout: { mode: "default" }, sections: { header: true, footer: true } };
}

/** The content the editor starts from: the draft when there is one, otherwise the published version. */
export function baseWork(detail: MessageTemplateDetail): Work {
  return workOf(detail.draftContent || detail.publishedContent, detail.channel);
}

export function toInput(w: Work): MessageTemplateContentInput {
  if (w.channel === "sms") return { channel: "sms", sms: { text: w.text } };
  return { channel: "email", email: { subject: w.subject, preheader: w.preheader, html: w.html, layout: w.layout, sections: { header: w.sections.header, footer: w.sections.footer } } };
}

export function sameWork(a: Work | null | undefined, b: Work | null | undefined): boolean {
  if (!a || !b) return a === b;
  return JSON.stringify(toInput(a)) === JSON.stringify(toInput(b));
}

/* ---------- status ---------- */

export function templateChips(row: MessageTemplateSummary, t: Tr): Chip[] {
  const out: Chip[] = [];
  if (row.managedBy === "platform") out.push({ tone: "neutral", label: t("messaging.builder.status.platformDefault", "Platform default") });
  else if (row.managedBy === "workspace_default") out.push({ tone: "neutral", label: t("messaging.builder.status.workspaceDefault", "Workspace default") });
  else if (row.published) out.push({ tone: "info", label: row.resettable?.kind === "workspace_default_group"
    ? t("messaging.builder.status.takenOver", "Taken over") : t("messaging.builder.status.customized", "Customized") });
  if (!row.published) out.push({ tone: "neutral", label: t("messaging.builder.status.draft", "Draft") });
  else {
    out.push({ tone: "success", label: t("messaging.builder.status.published", "Published") });
    if (row.draft) out.push({ tone: "warning", label: t("messaging.builder.status.unpublishedChanges", "Unpublished changes") });
  }
  if (row.enabled === false) out.push({ tone: "neutral", label: t("messaging.builder.status.notSending", "Not sending") });
  return out;
}

/** `inGroup`: the row sits under a "Default layouts" heading in the rail, which already says who provides it. */
export function layoutChips(row: MessageLayoutSummary, t: Tr, inGroup = false): Chip[] {
  if (row.archived) return [{ tone: "neutral", label: t("messaging.builder.status.archived", "Archived") }];
  const out: Chip[] = [];
  if (row.isDefault) out.push({ tone: "info", label: t("messaging.builder.status.globalDefault", "Global default") });
  const provided = layoutProvidedChip(row, t);
  if (provided && !inGroup) out.push(provided);
  if (row.published) out.push({ tone: "success", label: t("messaging.builder.status.published", "Published") });
  else if (!provided) out.push({ tone: "neutral", label: t("messaging.builder.status.draft", "Draft") });
  // A provided layout's only draft is one left from before layouts became read only.
  if (row.published && row.draft) out.push({ tone: "warning", label: t("messaging.builder.status.unpublishedChanges", "Unpublished changes") });
  return out;
}

/** A layout this company doesn't manage: changed by duplicating it. */
export function layoutIsProvided(row: Pick<MessageLayoutSummary, "managedBy">): boolean {
  return row.managedBy !== "company";
}

function layoutProvidedChip(row: MessageLayoutSummary, t: Tr): Chip | null {
  if (row.managedBy === "platform") return { tone: "neutral", label: t("messaging.builder.status.platform", "Platform") };
  if (row.managedBy === "workspace_default") return { tone: "neutral", label: t("messaging.builder.status.workspaceDefault", "Workspace default") };
  return null;
}

/** The rail shows status as badges; unsaved edits join them as a warning badge. */
export function railChips(chips: Chip[], unsaved: boolean, t: Tr): Chip[] {
  return unsaved ? [...chips, { tone: "warning", label: t("messaging.builder.unsavedEdits", "unsaved edits") }] : chips;
}

export function railSubtitle(chips: Chip[], unsaved: boolean, t: Tr): string {
  const parts = [chips.map((c) => c.label).join(", ")];
  if (unsaved) parts.push(t("messaging.builder.unsavedEdits", "unsaved edits"));
  return parts.filter(Boolean).join(" · ");
}

export function channelName(channel: "email" | "sms", t: Tr): string {
  return channel === "sms" ? t("messaging.builder.channel.sms", "SMS") : t("messaging.builder.channel.email", "Email");
}

/* ---------- catalogue grouping ---------- */

export interface CatalogueMessage {
  /**
   * What the rail shows as ONE message, with its email and SMS rows for the selected locale. It is a
   * presentation identity only: every API call uses the row's own `templateId` / `messageId`.
   * See `messagePresentationKey`.
   */
  key: string;
  name: string;
  groupId: string;
  groupLabel: string;
  rows: Partial<Record<"email" | "sms", MessageTemplateSummary>>;
}
export interface CatalogueGroup { id: string; label: string; messages: CatalogueMessage[] }

/**
 * The tenant feature (the composer's manifest `features`, served as the tenant profile's `modules`) each
 * catalogue group belongs to. A group whose feature is KNOWN absent is not shown: an events-only tenant
 * sees no appointment or queue messages. Unknown modules (null) hide nothing; the server's catalogue is
 * then the only filter.
 */
export const GROUP_MODULE: Readonly<Record<string, string>> = { appointments: "booking", queues: "queue", events: "events" }; // ui-config

/** Lineages edited elsewhere: the message Library keeps its own composer and draft lifecycle. */
const OTHER_EDITOR_LINEAGES: ReadonlySet<string> = new Set(["library"]); // ui-config

/**
 * The rail identity of a message, from a row's `messageId`.
 *
 * A Journey moment is served as one row PER CHANNEL (`asset_event_confirmation:email`, `…:sms`, the
 * contract's channel-row ids), and both are one message to an author: one rail item, with Email and SMS
 * tabs. The contract's own parser (`studioMessageJourneyMomentRowRef`) names the moment, and the moment's
 * asset id (which is also the legacy all-channel id) is the shared key. Every other row keeps its own
 * `messageId`: appointment communications already share one id across channels and differ by id for each
 * reminder offset and source, so they stay separate. Also accepts a deep link's `messageId` in either form.
 */
export function messagePresentationKey(messageId: string): string {
  const ref = studioMessageJourneyMomentRowRef(messageId);
  return ref ? studioMessageJourneyMomentAssetId(ref.moment) : messageId;
}

/** The channel a deep-linked `messageId` names on its own (a Journey channel row), else null. */
export function channelOfMessageId(messageId: string): "email" | "sms" | null {
  const ch = studioMessageJourneyMomentRowRef(messageId)?.channel ?? null;
  return ch === "email" || ch === "sms" ? ch : null;
}

/** One row locale, or every row locale one Language choice shows (see `catalogueLanguageViews`). */
export type LocaleScope = string | null | readonly (string | null)[];
const inScope = (scope: LocaleScope, l: string | null): boolean => (scope === null || typeof scope === "string" ? scope === l : scope.includes(l));

export function groupCatalogue(rows: MessageTemplateSummary[], locale: LocaleScope, hiddenGroup: (groupId: string) => boolean): CatalogueGroup[] {
  const groups = new Map<string, CatalogueGroup>();
  const messages = new Map<string, CatalogueMessage>();
  for (const row of rows) {
    if (OTHER_EDITOR_LINEAGES.has(row.lineage)) continue;
    if (!inScope(locale, row.locale ?? null)) continue;
    if (hiddenGroup(row.group.id)) continue;
    let g = groups.get(row.group.id);
    if (!g) { g = { id: row.group.id, label: row.group.label, messages: [] }; groups.set(row.group.id, g); }
    // A row whose channel is already filled under this key is a different source: it gets its own rail item
    // rather than replacing (and hiding) the first one.
    const base = messagePresentationKey(row.messageId);
    let key = base;
    for (let n = 2; messages.get(key)?.rows[row.channel]; n++) key = `${base}#${n}`;
    let m = messages.get(key);
    if (!m) { m = { key, name: row.name, groupId: g.id, groupLabel: g.label, rows: {} }; messages.set(key, m); g.messages.push(m); }
    m.rows[row.channel] = row;
  }
  return [...groups.values()].filter((g) => g.messages.length);
}

/* ---------- what the editor shows: an explicit target is never replaced by something else ---------- */

export type Channel = "email" | "sms";

/**
 * What the author asked to see. `explicit` marks a target that came from a link (or a deliberate choice) and must
 * never be swapped for a different message, channel or layout: if it can't be found, the screen says so.
 * Without an explicit target the builder opens the first message (and the email channel when there is one).
 */
export interface MessageTarget {
  key: string | null;
  templateId: string | null;
  channel: Channel;
  explicit: { message: boolean; channel: boolean };
}

export type MessageResolution =
  /** The catalogue has not loaded yet: show nothing else, keep the target. */
  | { kind: "pending" }
  /** The catalogue read failed: show the failure and a retry, keep the target. */
  | { kind: "failed" }
  /** The catalogue loaded and holds no message the builder edits. */
  | { kind: "empty" }
  | { kind: "selected"; message: CatalogueMessage; channel: Channel }
  /** The target exists, but in another language: switch the language list to it (that IS the target). */
  | { kind: "relocale"; locale: string | null }
  | { kind: "unavailable"; what: "message" | "template"; ref: string }
  /** The message exists but has no row for the requested channel. */
  | { kind: "unavailable"; what: "channel"; ref: string; message: CatalogueMessage; channel: Channel; available: Channel | null };

const rowsOf = (m: CatalogueMessage) => (["email", "sms"] as const).filter((c) => !!m.rows[c]);

/**
 * Resolve the target against the catalogue. Pure: the screen renders exactly what this returns.
 * Only a successfully loaded catalogue (`ready`, including a refresh of a loaded one) resolves anything.
 */
export function resolveMessageSelection(input: {
  phase: "loading" | "error" | "ready";
  rows: MessageTemplateSummary[];
  locale: LocaleScope;
  hiddenGroup: (groupId: string) => boolean;
  target: MessageTarget;
}): MessageResolution {
  const { phase, rows, locale, hiddenGroup, target } = input;
  if (phase === "loading") return { kind: "pending" };
  if (phase === "error") return { kind: "failed" };
  const inLocale = (l: LocaleScope) => groupCatalogue(rows, l, hiddenGroup).flatMap((g) => g.messages);
  const here = inLocale(locale);
  const withChannel = (m: CatalogueMessage, wanted: Channel, explicitChannel: boolean): MessageResolution => {
    if (m.rows[wanted]) return { kind: "selected", message: m, channel: wanted };
    const available = rowsOf(m)[0] ?? null;
    if (explicitChannel || !available) return { kind: "unavailable", what: "channel", ref: m.key, message: m, channel: wanted, available };
    return { kind: "selected", message: m, channel: available };
  };
  // A link to one template row: that exact row, in its own language and channel, or "unavailable".
  if (target.templateId) {
    const id = target.templateId;
    const holds = (m: CatalogueMessage) => rowsOf(m).find((c) => m.rows[c]?.templateId === id) ?? null;
    for (const m of here) { const c = holds(m); if (c) return { kind: "selected", message: m, channel: c }; }
    const row = rows.find((r) => r.templateId === id);
    const other = row ? (row.locale ?? null) : undefined;
    if (other !== undefined && !inScope(locale, other) && inLocale(other).some((m) => holds(m))) return { kind: "relocale", locale: other };
    return { kind: "unavailable", what: "template", ref: id };
  }
  if (target.key) {
    const m = here.find((x) => x.key === target.key);
    if (m) return withChannel(m, target.channel, target.explicit.channel);
    if (target.explicit.message) {
      const other = rows.find((r) => messagePresentationKey(r.messageId) === target.key && !inScope(locale, r.locale ?? null));
      if (other && inLocale(other.locale ?? null).some((x) => x.key === target.key)) return { kind: "relocale", locale: other.locale ?? null };
      return { kind: "unavailable", what: "message", ref: target.key };
    }
  }
  // No explicit target: the first message.
  return here.length ? withChannel(here[0], target.channel, false) : { kind: "empty" };
}

export type LayoutResolution =
  | { kind: "pending" } | { kind: "failed" } | { kind: "empty" }
  | { kind: "selected"; layoutKey: string }
  | { kind: "unavailable"; ref: string };

/** The layout to edit: the explicit one, or "unavailable"; only without an explicit target the global default. */
export function resolveLayoutSelection(input: {
  phase: "loading" | "error" | "ready";
  layouts: MessageLayoutSummary[];
  defaultKey: string | null;
  target: { key: string | null; explicit: boolean };
  /** Keys this screen just created, before the list refresh that will include them. */
  known?: ReadonlySet<string>;
}): LayoutResolution {
  const { phase, layouts, defaultKey, target, known } = input;
  if (target.key && known?.has(target.key)) return { kind: "selected", layoutKey: target.key };
  if (phase === "loading") return { kind: "pending" };
  if (phase === "error") return { kind: "failed" };
  if (target.key && layouts.some((l) => l.layoutKey === target.key)) return { kind: "selected", layoutKey: target.key };
  if (target.key && target.explicit) return { kind: "unavailable", ref: target.key };
  const pick = (defaultKey && layouts.find((l) => l.layoutKey === defaultKey)) || layouts.find((l) => !l.archived) || layouts[0];
  return pick ? { kind: "selected", layoutKey: pick.layoutKey } : { kind: "empty" };
}

/** Locales present in the catalogue, the tenant default (null) first. */
export function catalogueLocales(rows: MessageTemplateSummary[]): (string | null)[] {
  const seen: (string | null)[] = [];
  for (const r of rows) { if (OTHER_EDITOR_LINEAGES.has(r.lineage)) continue; const l = r.locale ?? null; if (!seen.includes(l)) seen.push(l); }
  return seen.sort((a, b) => (a === null ? -1 : b === null ? 1 : a.localeCompare(b)));
}

/** The Language list's value for the tenant-default rows. */
export const DEFAULT_LANGUAGE_VIEW = "__default__";
/** One choice in the Language list. */
export interface LanguageView {
  value: string;
  /** What to label it by; `null` = the tenant-default language. */
  locale: string | null;
  /** Every row locale this choice shows. */
  locales: (string | null)[];
}
/**
 * The Language list, from the languages the catalogue holds. The builder opens on the first choice, which is the
 * tenant's own language. A `null` row is in the tenant's default language, so when rows name at most ONE language,
 * that language and the default rows are the same language: ONE choice shows them all, and no list is shown. Two or
 * more named languages make the list (the rail shows it by itself); the default rows then keep their own choice,
 * first, because the screen cannot tell which named language the tenant default is.
 */
export function catalogueLanguageViews(rows: MessageTemplateSummary[]): LanguageView[] {
  const all = catalogueLocales(rows);
  const named = all.filter((l): l is string => l !== null);
  if (named.length <= 1) {
    const only = named[0] ?? null;
    return all.length ? [{ value: only ?? DEFAULT_LANGUAGE_VIEW, locale: only, locales: all }] : [];
  }
  return all.map((l) => ({ value: l ?? DEFAULT_LANGUAGE_VIEW, locale: l, locales: [l] }));
}

/* ---------- layouts for a template ---------- */

export interface LayoutOption { value: string; label: string; disabled?: boolean }
export const INHERIT = "__default__";

export function layoutLabel(row: MessageLayoutSummary | null | undefined, t: Tr): string {
  if (!row) return t("messaging.builder.layout.unknown", "an unknown layout");
  return row.published ? `${row.name} v${row.published.version}` : `${row.name} ${t("messaging.builder.layout.notPublishedSuffix", "(not published)")}`;
}

export function layoutOptions(layouts: MessageLayoutSummary[], def: MessagingDefaultLayout | null, binding: MessageTemplateLayoutBinding, t: Tr): LayoutOption[] {
  const defRow = def && def.layoutKey ? layouts.find((l) => l.layoutKey === def.layoutKey) : undefined;
  const out: LayoutOption[] = [{ value: INHERIT, label: `${t("messaging.builder.layout.useDefault", "Use global default")} (${defRow ? layoutLabel(defRow, t) : t("messaging.builder.layout.noDefault", "none selected")})` }];
  for (const l of layouts) if (!l.archived && l.published) out.push({ value: l.layoutKey, label: `${l.name} (${t("messaging.builder.layout.publishedV", "published")} v${l.published.version})` });
  if (binding.mode === "layout" && !out.some((o) => o.value === binding.layoutKey)) {
    const cur = layouts.find((l) => l.layoutKey === binding.layoutKey);
    out.push({ value: binding.layoutKey, label: `${cur ? cur.name : binding.layoutKey} ${t("messaging.builder.layout.chooseAnother", "(not available - choose another)")}`, disabled: true });
  }
  return out;
}

export function bindingValue(binding: MessageTemplateLayoutBinding): string { return binding.mode === "default" ? INHERIT : binding.layoutKey; }
export function bindingFromValue(value: string): MessageTemplateLayoutBinding { return value === INHERIT ? { mode: "default" } : { mode: "layout", layoutKey: value }; }

/** The layout an email is placed into: the override, or the tenant default. */
export function effectiveLayout(layouts: MessageLayoutSummary[], def: MessagingDefaultLayout | null, binding: MessageTemplateLayoutBinding): MessageLayoutSummary | null {
  const key = binding.mode === "layout" ? binding.layoutKey : def?.layoutKey ?? null;
  return key ? layouts.find((l) => l.layoutKey === key) ?? null : null;
}

export function bindingCopy(eff: MessageLayoutSummary | null, binding: MessageTemplateLayoutBinding, t: Tr): { tone: Tone; title: string; text: string } {
  const override = binding.mode === "layout";
  if (!eff || !eff.published || eff.archived) {
    return {
      tone: "warning",
      title: (override ? t("messaging.builder.binding.override", "Override: ") : t("messaging.builder.binding.inherits", "Inherits the global default: ")) + (eff ? layoutLabel(eff, t) : t("messaging.builder.layout.noDefault", "none selected")),
      text: t("messaging.builder.binding.unavailable", "This layout is not published, so it can't be used yet. Choose a published layout or publish this one first."),
    };
  }
  const label = layoutLabel(eff, t);
  const draftNote = eff.draft ? " " + t("messaging.builder.binding.draftNote", "The layout has newer saved changes that are not published, so they are not used yet.") : "";
  return override
    ? { tone: "neutral", title: `${t("messaging.builder.binding.override", "Override: ")}${label}`, text: t("messaging.builder.binding.overrideText", "This message uses this layout's newest published version. Publishing the layout again updates this message too; changing the global default does not.") + draftNote }
    : { tone: "info", title: `${t("messaging.builder.binding.inherits", "Inherits the global default: ")}${label}`, text: t("messaging.builder.binding.inheritText", "This message follows whichever layout is the global default. Changing the global default changes this message.") + draftNote };
}

/* ---------- next-send wording (operator rule: say exactly what publishing does) ---------- */

export function publishConsequence(t: Tr): string {
  return t("messaging.builder.publish.consequence",
    "Messages prepared from now on use the new version, including messages for bookings and registrations that already exist. Messages that were already prepared keep the content they were created with. Saving a draft never changes what sends.");
}

export function layoutPublishConsequence(t: Tr): string {
  return t("messaging.builder.publishLayout.consequence",
    "Every email that uses this layout gets the new version from the next message prepared, including messages for bookings and registrations that already exist. Messages that were already prepared keep the layout they were created with.");
}

/* ---------- small formatting ---------- */

export function versionWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** 1-based line / column (as the server reports an issue) to a string offset in the source. */
export function offsetOf(src: string, line: number, column: number): number {
  let off = 0;
  for (let l = 1; l < line; l++) { const nl = src.indexOf("\n", off); if (nl < 0) return src.length; off = nl + 1; }
  return Math.min(src.length, off + Math.max(0, column - 1));
}

export function problemsTitle(n: number, t: Tr): string {
  return n === 1 ? t("messaging.builder.problems.one", "1 problem blocks publishing") : `${n} ${t("messaging.builder.problems.many", "problems block publishing")}`;
}
